'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';

export default function FeatureCards() {
    const sectionRef = useRef<HTMLElement>(null);
    const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
    const t = useTranslations('FeatureCards');

    const features = [
        {
            title: t('digital_clone.title'),
            subtitle: t('digital_clone.subtitle'),
            description: t('digital_clone.description'),
            href: '/clon-digital'
        },
        {
            title: t('social_thermometer.title'),
            subtitle: t('social_thermometer.subtitle'),
            description: t('social_thermometer.description'),
            href: '/termometro-social'
        },
        {
            title: t('digital_shield.title'),
            subtitle: t('digital_shield.subtitle'),
            description: t('digital_shield.description'),
            href: '/escudo-digital'
        },
        {
            title: t('electoral_mapping.title'),
            subtitle: t('electoral_mapping.subtitle'),
            description: t('electoral_mapping.description'),
            href: '/mapeo-electoral'
        },
        {
            title: t('chatbot.title'),
            subtitle: t('chatbot.subtitle'),
            description: t('chatbot.description'),
            href: '/chatbot'
        },
        {
            title: t('training.title'),
            subtitle: t('training.subtitle'),
            description: t('training.description'),
            href: '/capacitacion'
        }
    ];

    return (
        <motion.section
            ref={sectionRef}
            className=" py-8 sm:py-12 lg:py-16 relative w-full"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
        >

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <motion.div
                    className="flex flex-col items-center justify-center"
                    initial={{ opacity: 0, y: -50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -50 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <motion.h1
                        className="text-4xl font-bold text-blue-600 dark:text-blue-400 md:-mt-8 mb-4 text-center"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                    >
                        {t('main_title')}
                    </motion.h1>
                    <motion.p
                        className="text-3xl font-normal text-red-600 dark:text-red-400 mb-14 italic"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                    >
                        {t('subtitle')}
                    </motion.p>
                </motion.div>
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10"
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.3,
                                delayChildren: 0.2
                            }
                        }
                    }}
                >

                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            variants={{
                                hidden: {
                                    opacity: 0,
                                    y: 60,
                                    scale: 0.8,
                                    rotateX: -15
                                },
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    scale: 1,
                                    rotateX: 0,
                                    transition: {
                                        duration: 0.8,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                        type: "spring",
                                        stiffness: 100,
                                        damping: 15
                                    }
                                }
                            }}
                            whileHover={{
                                y: -10,
                                scale: 1.05,
                                transition: {
                                    duration: 0.3,
                                    ease: "easeOut"
                                }
                            }}
                            whileTap={{
                                scale: 0.98
                            }}
                        >
                            <motion.div
                                whileHover={{
                                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                                    transition: { duration: 0.3 }
                                }}
                                className="dark:[&:hover]:shadow-2xl"
                            >
                                <Link href={feature.href}>
                                    <Card className="bg-gradient-to-b from-gray-100 to-gray-300 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-lg rounded-lg flex flex-col h-full hover:shadow-xl dark:hover:shadow-2xl transition-all duration-300 cursor-pointer">
                                        <CardHeader className="pb-4">
                                            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                                {feature.title}
                                            </CardTitle>
                                            <CardDescription className="text-blue-600 dark:text-blue-400 italic text-base">
                                                {feature.subtitle}
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="pt-0 flex-grow flex flex-col justify-between">
                                            <p className="text-gray-700 dark:text-gray-300 mb-6 text-base leading-relaxed">
                                                {feature.description}
                                            </p>
                                            <div className="flex justify-end mt-auto">
                                                <motion.div
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <Button className="bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white rounded-md shadow-sm dark:shadow-md transition-all duration-300 hover:shadow-lg dark:hover:shadow-xl">
                                                        {t('more_info')}
                                                    </Button>
                                                </motion.div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </motion.div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </motion.section>
    );
}
